/**
 * LLM adapter — ARCHITECTURE.md section 2.3.
 * Unified interface for all LLM interactions in the investigation pipeline.
 *
 * Responsibilities:
 * - Model routing: Haiku for relevance, Sonnet for extraction, Opus for synthesis
 * - JSON parsing with retry (3 attempts with increasing specificity)
 * - Token counting per call, cumulative per investigation
 * - Provider fallback via the existing fallback chain
 */

import type { AICompletionRequest } from './providers/base-provider';
import type { EntityType, RelationshipType } from '@/intelligence/entities/types';
import type { PriorityIndicator, OntologyExtensions } from '@/config/desk-config';
import { executeWithFallback, type FallbackOptions } from './fallback-chain';
import { toStructuredOutputSchema } from './schemas/to-structured-output';
import type { z } from 'zod';
import { RelevanceResultSchema } from './schemas/relevance';
import { EntityExtractionResultSchema } from './schemas/entities';
import { RelationshipResultSchema } from './schemas/relationships';
import { DraftAssessmentSchema } from './schemas/synthesis';

// ---------------------------------------------------------------------------
// Model configuration
// ---------------------------------------------------------------------------

/** Model tiers for different pipeline stages */
export type ModelTier = 'fast' | 'standard' | 'large';

/** Default model assignments per tier */
const DEFAULT_MODELS: Record<ModelTier, string> = {
  fast: 'claude-haiku-4-5-20251001',   // Relevance filtering (no newer Haiku exists)
  standard: 'claude-sonnet-5',          // Entity extraction, relationships
  large: 'claude-opus-5',               // Synthesis
};

// ---------------------------------------------------------------------------
// LLM output types — raw data as returned by the model
// ---------------------------------------------------------------------------

/** Raw entity as returned by the LLM (before enrichment with source metadata) */
export interface RawExtractedEntity {
  type: EntityType;
  name: string;
  description?: string;
  aliases?: string[];
  properties?: Record<string, unknown>;
  extractionQuote: string;
  confidence: number;
}

/** Raw relationship as returned by the LLM */
export interface RawExtractedRelationship {
  sourceEntity: string;   // entity name (matched by caller)
  targetEntity: string;   // entity name (matched by caller)
  type: RelationshipType;
  confidence: number;
  evidenceQuote: string;
}

/** Relevance score output from the filter */
export interface RelevanceResult {
  score: number;               // 0-1
  matchedIndicatorIds: string[];
}

/** Raw synthesis output (before enrichment with investigation metadata) */
export interface RawDraftAssessment {
  executiveSummary: string;
  sections: Array<{
    title: string;
    content: string;
    citations: string[];  // entity IDs
  }>;
}

/** Result of a single LLM call with token tracking */
export interface LLMCallResult<T> {
  data: T;
  tokensUsed: number;
  model: string;
  provider: string;
  latencyMs: number;
}

// ---------------------------------------------------------------------------
// Token tracker — cumulative per investigation
// ---------------------------------------------------------------------------

interface TokenRecord {
  model: string;
  tokens: number;
  timestamp: string;
}

export class TokenTracker {
  private _totalTokens = 0;
  private _callCount = 0;
  private readonly _calls: TokenRecord[] = [];

  get totalTokens(): number { return this._totalTokens; }
  get callCount(): number { return this._callCount; }

  record(model: string, tokens: number): void {
    this._totalTokens += tokens;
    this._callCount++;
    this._calls.push({ model, tokens, timestamp: new Date().toISOString() });
  }

  reset(): void {
    this._totalTokens = 0;
    this._callCount = 0;
    this._calls.length = 0;
  }

  getSummary(): { totalTokens: number; callCount: number; byModel: Record<string, number> } {
    const byModel: Record<string, number> = {};
    for (const call of this._calls) {
      byModel[call.model] = (byModel[call.model] ?? 0) + call.tokens;
    }
    return { totalTokens: this._totalTokens, callCount: this._callCount, byModel };
  }
}

// ---------------------------------------------------------------------------
// JSON parsing with retry
// ---------------------------------------------------------------------------

const MAX_JSON_RETRIES = 3;

/**
 * Extract JSON from LLM response text.
 * Handles markdown code blocks, leading/trailing commentary.
 */
export function extractJSON(text: string): string | null {
  const trimmed = text.trim();

  // Direct parse — text is already pure JSON
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      JSON.parse(trimmed);
      return trimmed;
    } catch {
      // Fall through to other strategies
    }
  }

  // Markdown code block: ```json ... ``` or ``` ... ```
  const codeBlockMatch = trimmed.match(/```(?:json)?\s*\n?([\s\S]*?)\n?\s*```/);
  if (codeBlockMatch?.[1]) {
    const inner = codeBlockMatch[1].trim();
    try {
      JSON.parse(inner);
      return inner;
    } catch {
      // Fall through
    }
  }

  // Find the outermost { ... } or [ ... ] block
  const start = trimmed.indexOf('{');
  const startArr = trimmed.indexOf('[');
  const startIdx = start === -1 ? startArr : startArr === -1 ? start : Math.min(start, startArr);

  if (startIdx !== -1) {
    const isArray = trimmed[startIdx] === '[';
    const open = isArray ? '[' : '{';
    const close = isArray ? ']' : '}';
    let depth = 0;
    let inString = false;
    let escape = false;

    for (let i = startIdx; i < trimmed.length; i++) {
      const ch = trimmed[i];

      if (escape) {
        escape = false;
        continue;
      }
      if (ch === '\\' && inString) {
        escape = true;
        continue;
      }
      if (ch === '"') {
        inString = !inString;
        continue;
      }
      if (inString) continue;

      if (ch === open) depth++;
      else if (ch === close) {
        depth--;
        if (depth === 0) {
          const candidate = trimmed.slice(startIdx, i + 1);
          try {
            JSON.parse(candidate);
            return candidate;
          } catch {
            return null;
          }
        }
      }
    }
  }

  return null;
}

/**
 * Build a retry prompt after a malformed response.
 */
function buildRetryPrompt(originalPrompt: string, attempt: number, badResponse: string): string {
  const truncated = badResponse.length > 300
    ? badResponse.slice(0, 300) + '...'
    : badResponse;

  if (attempt === 1) {
    return (
      originalPrompt +
      '\n\nIMPORTANT: Your previous response was not valid JSON. ' +
      'Return ONLY a valid JSON object. No commentary, no markdown, no explanation.'
    );
  }

  return (
    originalPrompt +
    '\n\nCRITICAL: Your previous response was malformed:\n"' +
    truncated +
    '"\n\nReturn ONLY valid JSON matching the schema. Start with { and end with }. No other text.'
  );
}

/**
 * Call the LLM and parse the response as JSON.
 * Retries up to 3 times with increasingly specific prompts on malformed JSON.
 * Retained as fallback for providers without structured output support.
 */
export async function callAndParseJSON<T>(
  systemPrompt: string,
  userPrompt: string,
  model: string,
  tracker: TokenTracker | null,
  options?: FallbackOptions,
): Promise<LLMCallResult<T>> {
  let lastError: Error | null = null;
  let currentUserPrompt = userPrompt;

  for (let attempt = 0; attempt < MAX_JSON_RETRIES; attempt++) {
    const request: AICompletionRequest = {
      systemPrompt,
      prompt: currentUserPrompt,
      model,
      maxTokens: 4096,
      temperature: 0.1,
    };

    const response = await executeWithFallback(request, options);
    const tokens = response.tokensUsed ?? 0;

    if (tracker) {
      tracker.record(response.model, tokens);
    }

    const jsonText = extractJSON(response.text);

    if (!jsonText) {
      lastError = new Error(`Attempt ${attempt + 1}: No valid JSON found in LLM response`);
      currentUserPrompt = buildRetryPrompt(userPrompt, attempt + 1, response.text);
      continue;
    }

    try {
      const parsed = JSON.parse(jsonText) as T;
      return {
        data: parsed,
        tokensUsed: tokens,
        model: response.model,
        provider: response.provider,
        latencyMs: response.latencyMs,
      };
    } catch (parseErr) {
      lastError = new Error(
        `Attempt ${attempt + 1}: JSON parse failed: ${parseErr instanceof Error ? parseErr.message : String(parseErr)}`,
      );
      currentUserPrompt = buildRetryPrompt(userPrompt, attempt + 1, response.text);
    }
  }

  throw new Error(
    `Failed to get valid JSON after ${MAX_JSON_RETRIES} attempts. Last error: ${lastError?.message ?? 'unknown'}`,
  );
}

// ---------------------------------------------------------------------------
// Structured output call — uses Zod schema + provider structured outputs
// ---------------------------------------------------------------------------

/**
 * Call the LLM with a Zod schema for structured output.
 * If the provider supports structured outputs (Anthropic, OpenRouter),
 * the response is guaranteed schema-valid. Otherwise falls back to
 * callAndParseJSON + Zod validation.
 */
async function callWithSchema<T>(
  systemPrompt: string,
  userPrompt: string,
  schema: z.ZodType<T>,
  model: string,
  maxTokens: number,
  tracker: TokenTracker | null,
  options?: FallbackOptions,
  effort?: AICompletionRequest['effort'],
): Promise<LLMCallResult<T>> {
  const jsonSchema = toStructuredOutputSchema(schema);
  const request: AICompletionRequest = {
    systemPrompt,
    prompt: userPrompt,
    model,
    maxTokens,
    temperature: 0.1,
    outputSchema: jsonSchema,
    effort,
  };

  const response = await executeWithFallback(request, options);
  const tokens = response.tokensUsed ?? 0;

  if (tracker) {
    tracker.record(response.model, tokens);
  }

  // Provider returned structured output — validate with Zod
  if (response.structuredOutput != null) {
    const parsed = schema.parse(response.structuredOutput);
    return {
      data: parsed,
      tokensUsed: tokens,
      model: response.model,
      provider: response.provider,
      latencyMs: response.latencyMs,
    };
  }

  // Fallback: provider didn't support structured output, parse text as JSON
  const jsonText = extractJSON(response.text);
  if (!jsonText) {
    throw new Error('No valid JSON found in LLM response (structured output fallback)');
  }
  const raw = JSON.parse(jsonText);
  const parsed = schema.parse(raw);
  return {
    data: parsed,
    tokensUsed: tokens,
    model: response.model,
    provider: response.provider,
    latencyMs: response.latencyMs,
  };
}

// ---------------------------------------------------------------------------
// LLM Adapter interface
// ---------------------------------------------------------------------------

/** Unified LLM adapter interface — ARCHITECTURE.md section 2.3 */
export interface LLMAdapter {
  classifyRelevance(
    text: string,
    indicators: PriorityIndicator[],
  ): Promise<LLMCallResult<RelevanceResult>>;

  extractEntities(
    text: string,
    entityTypes: EntityType[],
    ontologyExtensions?: OntologyExtensions,
  ): Promise<LLMCallResult<{ entities: RawExtractedEntity[] }>>;

  identifyRelationships(
    entities: Array<{ name: string; type: EntityType }>,
    context: string,
    allowedTypes: RelationshipType[],
  ): Promise<LLMCallResult<{ relationships: RawExtractedRelationship[] }>>;

  synthesize(
    graphSummary: string,
    template: string,
  ): Promise<LLMCallResult<RawDraftAssessment>>;
}

/** Configuration for the LLM adapter */
export interface LLMAdapterConfig {
  models?: Partial<Record<ModelTier, string>>;
  providers?: string[];
  timeout?: number;
}

/**
 * Create a configured LLM adapter instance.
 * Each investigation should create its own adapter with its own token tracker.
 */
export function createLLMAdapter(
  tracker: TokenTracker,
  config?: LLMAdapterConfig,
): LLMAdapter {
  const models = { ...DEFAULT_MODELS, ...config?.models };
  const baseFallbackOptions: FallbackOptions = {
    providers: config?.providers ?? ['anthropic', 'openrouter', 'ollama'],
    timeout: config?.timeout ?? 60_000,
    retries: 1,
  };

  return {
    async classifyRelevance(text, indicators) {
      const { buildRelevanceFilterPrompt } = await import('./prompts/extraction');
      const { system, user } = buildRelevanceFilterPrompt(text, indicators);
      return callWithSchema(
        system, user, RelevanceResultSchema, models.fast, 4096,
        tracker, baseFallbackOptions,
      );
    },

    async extractEntities(text, entityTypes, ontologyExtensions) {
      const { buildEntityExtractionPrompt } = await import('./prompts/extraction');
      const { system, user } = buildEntityExtractionPrompt(text, entityTypes, ontologyExtensions);
      // Sonnet 5 runs adaptive thinking by default; effort 'low' bounds the
      // thinking spend on this high-volume call, and the raised max_tokens
      // leaves room for it (thinking counts toward the output budget).
      return callWithSchema(
        system, user, EntityExtractionResultSchema, models.standard, 8192,
        tracker, { ...baseFallbackOptions, timeout: config?.timeout ?? 90_000 },
        'low',
      );
    },

    async identifyRelationships(entities, context, allowedTypes) {
      const { buildRelationshipPrompt } = await import('./prompts/extraction');
      const { system, user } = buildRelationshipPrompt(entities, context, allowedTypes);
      const result = await callWithSchema(
        system, user, RelationshipResultSchema, models.standard, 8192,
        tracker, baseFallbackOptions,
        'low',
      );
      // Cast: Zod schema uses z.string() for type, but interface uses RelationshipType
      return result as unknown as LLMCallResult<{ relationships: RawExtractedRelationship[] }>;
    },

    async synthesize(graphSummary, template) {
      const { buildSynthesisPrompt } = await import('./prompts/extraction');
      const { system, user } = buildSynthesisPrompt(graphSummary, template);
      return callWithSchema(
        system, user, DraftAssessmentSchema, models.large, 8192,
        tracker, { ...baseFallbackOptions, timeout: config?.timeout ?? 120_000 },
      );
    },
  };
}
